import type { GetServerSideProps, NextPage } from "next";
import {
  getGlobals,
  getGraphqlSdk,
  GraphQLResponseError
} from "@src/lib/services/server/contentful";
import { sortBlogTopicPosts } from "@src/lib/utils";
import type {
  BlogTopicQuery,
  BlogTopicQueryVariables
} from "@src/lib/services/server/contentful/generated/graphqlSdk";
import { logError } from "@src/lib/services/server/logger";
import "setimmediate";
import { BlogTopicPage, BlogTopicPageProps } from "@src/components";
import { setCacheHeaders } from "@src/lib/utils/pageCache";
import { PageCacheGroups } from "@src/lib/types/fastly";

type BlogTopicParams = {
  slug: string;
};

export const BlogTopic: NextPage<BlogTopicPageProps> = props => {
  return <BlogTopicPage {...props} />;
};

export const getServerSideProps: GetServerSideProps<
  BlogTopicPageProps,
  BlogTopicParams
> = async context => {
  const { params, preview = false } = context;
  const props: BlogTopicPageProps = {};

  if (!params) {
    return { notFound: true };
  }

  const { slug } = params;
  const query: BlogTopicQueryVariables = {
    slug,
    preview
  };
  const graphqlSdk = getGraphqlSdk(preview);
  const blogTopicQuery = graphqlSdk.blogTopic(query);
  const blogNavigationQuery = graphqlSdk.blogNavigation();
  const globalsQuery = getGlobals(preview);

  Object.assign(props, await globalsQuery);

  setCacheHeaders(PageCacheGroups.BLOGS, context);

  try {
    props.entry = await blogTopicQuery;
  } catch (e) {
    const error = e as GraphQLResponseError<BlogTopicQuery>;
    if (error?.response?.data) {
      props.entry = error.response.data;
    } else {
      logError(error);
      return { props };
    }
  }

  try {
    props.blogNavigation = await blogNavigationQuery;
  } catch (error) {
    logError(error);
    props.blogNavigation = undefined;
  }

  if (
    !props.entry.topicCollection?.items ||
    props.entry.topicCollection.items.length === 0
  ) {
    return { notFound: true };
  }

  // sort blogPosts by date
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (props.entry) {
    sortBlogTopicPosts(props.entry, 0, 5);
  }

  return { props };
};

export default BlogTopic;
